import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/* ---------------- DATA ---------------- */
const features = [
  {
    titleKey: "sticky_feature_1_title",
    descriptionKey: "sticky_feature_1_description",
    imageUrl: "/scroll1.webp",
    bgColor: "bg-[#002f37] shadow-lg",
    textColor: "text-white"
  },
  {
    titleKey: "sticky_feature_2_title",
    descriptionKey: "sticky_feature_2_description",
    imageUrl: "/hero3.webp",
    bgColor: "bg-[#002f37] shadow-lg",
    textColor: "text-white"
  },
  {
    titleKey: "sticky_feature_3_title",
    descriptionKey: "sticky_feature_3_description",
    imageUrl: "/weather.webp",
    bgColor: "bg-[#002f37] shadow-lg",
    textColor: "text-white"
  },
  {
    titleKey: "sticky_feature_4_title",
    descriptionKey: "sticky_feature_4_description",
    imageUrl: "/scada.webp",
    bgColor: "bg-[#002f37] shadow-lg",
    textColor: "text-white"
  },
];

/* ---------------- HOOK ---------------- */
const useScrollAnimation = (): [
  React.RefObject<HTMLHeadingElement>,
  boolean
] => {
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLHeadingElement>(null) as React.RefObject<HTMLHeadingElement>;

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1 }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return [ref, inView];
};

/* ---------------- HEADER ---------------- */
const AnimatedHeader: React.FC = () => {
  const { t } = useTranslation();
  const [headerRef, headerInView] = useScrollAnimation();

  return (
    <div className="text-center max-w-3xl mx-auto mb-16">
      <h2
        ref={headerRef}
        className={`text-3xl md:text-5xl font-bold transition-all duration-700 ease-out text-[#002f37] leading-tight px-4 md:px-0
        ${headerInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
      >
        {t("sticky_header_title")}
      </h2>
    </div>
  );
};

/* ---------------- MAIN ---------------- */
export function StickyFeatureSection() {
  const { t } = useTranslation();

  return (
    <div className="bg-white font-sans">
      <div className="px-4 md:px-[5%]">
        <div className="max-w-7xl mx-auto">
          <section className="py-12 md:py-24 flex flex-col items-center">
            <AnimatedHeader />

            <div className="w-full">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`${feature.bgColor} grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 p-4 md:p-10 rounded-2xl md:rounded-3xl mb-8 md:mb-16 md:sticky`}
                  style={{ top: window.innerWidth >= 768 ? "100px" : "0" }}
                >
                  {/* TEXT */}
                  <div className="order-2 md:order-1">
                    <h3 className="text-xl md:text-4xl font-bold mb-3 md:mb-4 text-white leading-tight">
                      {t(feature.titleKey)}
                    </h3>
                    <p className={`${feature.textColor} text-sm md:text-base leading-relaxed`}>
                      {t(feature.descriptionKey)}
                    </p>
                  </div>

                  {/* IMAGE */}
                  <div className="order-1 md:order-2">
                    <img
                      src={feature.imageUrl}
                      alt={t(feature.titleKey)}
                      loading="lazy"
                      className="w-full h-[220px] md:h-[330px] rounded-lg shadow-lg object-fit"
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        img.onerror = null;
                        img.src =
                          "https://placehold.co/600x400/cccccc/ffffff?text=Image+Not+Found";
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}